/**
 * @file CreateIndividualPage.js
 * @description This component provides a two-step wizard interface for creating a new individual.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import { createIndividual } from '../../services/individualService';
import Spinner from '../ui/spinners/Spinner';
import styles from './IndividualPage.module.scss';
import Toast from '../ui/toasts/Toast';
import PersonalTab from './PersonalTab';
import JobTab from './JobTab';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
import { useAuthContext } from '../../store/Contexts';
const TABS = {
    PERSONAL: 'Personal',
    JOB: 'Job',
};

const CreateIndividualPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(TABS.PERSONAL);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [populationGroups, setPopulationGroups] = useState([]);
    const {
        fieldNamingMap,
        defaultFieldNamingMap,
        enableEmailAndPhoneValidation,
    } = useAuthContext();
    const [isTestRecord, setIsTestRecord] = useState(false);
    const [formData, setFormData] = useState({
        personal: {},
        job: {},
    });
    const [personalTabValid, setPersonalTabValid] = useState(false);

    const personalTabRef = useRef(null);
    const jobTabRef = useRef(null);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const groups = await fetchPopulationGroups();
            const optionGroup = groups.map(group => ({
                id: group.id,
                name: group.name,
            }));
            setPopulationGroups(optionGroup);
        } catch (error) {
            console.error('Could not fetch required data', error);
            Toast.error(getText('msg_59d111678ed3'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelClick = () => {
        navigate(ROUTES.INDIVIDUALS);
    };

    const handlePersonalDataChange = (data, isValid) => {
        setFormData(prev => ({
            ...prev,
            personal: data,
        }));
        setPersonalTabValid(isValid);
    };

    const handleJobDataChange = data => {
        setFormData(prev => ({
            ...prev,
            job: data,
        }));
    };

    const handleNextClick = () => {
        if (activeTab === TABS.PERSONAL) {
            const isValid = personalTabRef.current?.validateForm();
            if (isValid) {
                setActiveTab(TABS.JOB);
            }
        }
    };

    const handlePreviousClick = () => {
        if (activeTab === TABS.JOB) {
            setActiveTab(TABS.PERSONAL);
        }
    };

    const handleTabClick = tab => {
        if (tab === TABS.JOB && activeTab === TABS.PERSONAL) {
            const isValid = personalTabRef.current?.validateForm();
            if (!isValid) {
                return;
            }
        }
        setActiveTab(tab);
    };

    const handleAdd = async () => {
        const jobValidation = jobTabRef.current?.validateForm();
        if (!jobValidation) {
            return;
        }

        try {
            setIsSaving(true);
            const tenantId = +window.localStorage.getItem('tenantId');

            const payload = {
                ...formData.personal,
                ...formData.job,
                tenantId,
                isTestRecord,
            };

            // Strip leading '+' from any phone number fields before sending
            Object.keys(payload).forEach(key => {
                if (key.includes('PhoneNumber') && payload[key]) {
                    payload[key] = payload[key].replace('+', '');
                }
            });

            await createIndividual(payload);
            Toast.success(getText('msg_106206f84576'));
            navigate(ROUTES.INDIVIDUALS);
        } catch (error) {
            console.error('Error creating individual:', error);
            if (error?.response?.data?.detail) {
                Toast.error(error.response.data.detail);
            } else {
                Toast.error(getText('msg_b13d01d9fb39'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleTestRecordChange = event => {
        setIsTestRecord(event.target.checked);
    };

    const renderTabNavigation = () => {
        return (
            <ul className={styles.wizardTabList}>
                <li
                    className={`${styles.wizardTabItem} ${
                        activeTab === TABS.PERSONAL
                            ? styles.wizardTabActive
                            : ''
                    }`}
                    onClick={() => handleTabClick(TABS.PERSONAL)}
                >
                    {TABS.PERSONAL}
                </li>
                <li className={styles.wizardChevron}>
                    <i className="bi bi-chevron-right" />
                </li>
                <li
                    className={`${styles.wizardTabItem} ${
                        activeTab === TABS.JOB ? styles.wizardTabActive : ''
                    } ${!personalTabValid ? styles.wizardTabDisabled : ''}`}
                    onClick={() => handleTabClick(TABS.JOB)}
                >
                    {TABS.JOB}
                </li>
            </ul>
        );
    };

    const renderFooter = () => {
        const isPersonalTab = activeTab === TABS.PERSONAL;
        const isJobTab = activeTab === TABS.JOB;

        return (
            <div className={styles.wizardFooter}>
                <div
                    className={`d-flex align-items-center w-100 ${
                        isPersonalTab
                            ? 'justify-content-end'
                            : 'justify-content-between'
                    }`}
                >
                    <div className={styles.wizardFooterLeft}>
                        {isPersonalTab && (
                            <div className="form-check d-flex align-items-center">
                                <input
                                    id="isTestRecord"
                                    type="checkbox"
                                    className="form-check-input me-2"
                                    checked={isTestRecord}
                                    onChange={handleTestRecordChange}
                                />
                                <label
                                    htmlFor="isTestRecord"
                                    className="form-check-label"
                                >
                                    {getText('msg_3428388712f5')}
                                </label>
                            </div>
                        )}
                        {isJobTab && (
                            <button
                                type="button"
                                onClick={handlePreviousClick}
                                className="ing-popup-blue-negative"
                            >
                                <i className="bi bi-chevron-left me-2" />
                                {getText('msg_50f94286ba30')}
                            </button>
                        )}
                    </div>
                    <div className={styles.wizardFooterRight}>
                        {isPersonalTab && (
                            <button
                                type="button"
                                onClick={handleNextClick}
                                className="button-base ing-button-primary-left"
                            >
                                {getText('msg_bc981983e7f5')}
                                <i className="bi bi-chevron-right ms-2" />
                            </button>
                        )}
                        {isJobTab && (
                            <button
                                type="button"
                                onClick={handleAdd}
                                className="button-base ing-button-primary-left"
                                disabled={isSaving}
                            >
                                {isSaving ? 'Adding...' : 'Add'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderTabContent = () => {
        if (activeTab === TABS.PERSONAL) {
            return (
                <PersonalTab
                    ref={personalTabRef}
                    individualId={null}
                    individualRecord={formData.personal}
                    tenantUser={null}
                    onSave={() => {}}
                    onDataChange={handlePersonalDataChange}
                    fieldNamingMap={fieldNamingMap}
                    defaultFieldNamingMap={defaultFieldNamingMap}
                    enableEmailAndPhoneValidation={
                        enableEmailAndPhoneValidation
                    }
                    isCreateMode={true}
                />
            );
        }

        if (activeTab === TABS.JOB) {
            return (
                <JobTab
                    ref={jobTabRef}
                    individualId={null}
                    individualRecord={formData.job}
                    tenantUser={null}
                    onSave={() => {}}
                    onDataChange={handleJobDataChange}
                    fieldNamingMap={fieldNamingMap}
                    defaultFieldNamingMap={defaultFieldNamingMap}
                    isCreateMode={true}
                />
            );
        }

        return null;
    };

    return (
        <div className={`page-content ${styles.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center pb-3">
                        <p className="page-title float-start card-title ms-4">
                            {getText('msg_d413c7b5f1ae')}
                        </p>

                        {renderTabNavigation()}

                        <div className="float-end d-flex align-items-center">
                            <span
                                type="button"
                                onClick={handleCancelClick}
                                className="ing-text-buttons mx-3 ms-3 me-4"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>

                    {isLoading ? (
                        <Spinner width={50} className={styles.loadingSpinner} />
                    ) : (
                        <>
                            <div
                                className={`mt-4 pt-2 ${styles.createTabContent}`}
                            >
                                {renderTabContent()}
                            </div>
                            {renderFooter()}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateIndividualPage;
